import { addMonths, addYears, convertYearToDate } from "@/utils/date";
import { getShortId } from "@/utils/id";
import { atom } from "jotai";
import { focusAtom } from "jotai-optics";
import { atomFamily } from "jotai/utils";
import { modelAtom } from "./model";
import { DollarCell, Epoch } from "./types";

export const createEpochAtoms = (
  modelPropId: "workingCapitalEpochs" | "epochs",
) => {
  const epochsFocusAtom = focusAtom(modelAtom, (optic) =>
    optic.prop(modelPropId),
  );

  const epochsAtomCache = atomFamily((epochId: string) =>
    atom(
      (get) => {
        const epoch = get(
          focusAtom(epochsFocusAtom, (optic) => optic.prop(epochId)),
        );

        return {
          ...epoch,
          date: epoch?.date || convertYearToDate(epoch?.year || 0).getTime(),
        };
      },
      (_, set, epoch: Epoch) => {
        set(
          focusAtom(epochsFocusAtom, (optic) => optic.prop(epochId)),
          { ...epoch, updatedAt: Date.now() },
        );
      },
    ),
  );

  const getEpochStartYearDate = (epoch: Epoch) => {
    if (
      (epoch.type === "partial" || epoch.type === "trailingTwelveMonths") &&
      epoch.duration?.startDate
    ) {
      return new Date(epoch.duration.startDate);
    }

    if (!epoch.date && epoch.year) {
      // convert year to date
      return convertYearToDate(epoch.year);
    }

    return new Date(epoch.date);
  };

  const orderedEpochsMolecule = atom((get) => {
    const epochsMap = get(epochsFocusAtom) || [];

    // sort by start year then full, partial, trailingTwelveMonths, then by id hash
    return Object.values(epochsMap)
      .sort((a, b) => {
        const yearA = getEpochStartYearDate(a).getTime();
        const yearB = getEpochStartYearDate(b).getTime();

        if (yearA !== yearB) {
          return yearA - yearB;
        }

        const typeOrder = { full: 0, partial: 1, trailingTwelveMonths: 2 };

        if (a.type !== b.type) {
          return typeOrder[a.type] - typeOrder[b.type];
        }

        //if partial order by month count
        if (a.type === "partial" && b.type === "partial") {
          const aMonthCount = epochMonthCount(a);
          const bMonthCount = epochMonthCount(b);
          if (aMonthCount !== bMonthCount) {
            return aMonthCount - bMonthCount;
          }
        }

        return (
          convertStringToNumberHash(a.id) - convertStringToNumberHash(b.id)
        );
      })
      .map((epoch) => epochsAtomCache(epoch.id));
  });

  const createEpochAtom = atom(
    null,
    (
      get,
      set,
      epoch?: Omit<Epoch, "id">,
      incrementFnType?: "monthly" | "annually" | "quarterly",
    ) => {
      const epochId = getShortId();

      set(modelAtom, (prev) => {
        const epochAtoms = get(orderedEpochsMolecule);

        const incrementFunction =
          incrementFnType === "monthly" || incrementFnType === "quarterly"
            ? addMonths
            : addYears;

        const date =
          epochAtoms.length > 0
            ? incrementFunction(
                get(epochAtoms[epochAtoms.length - 1]).date,
                incrementFnType === "quarterly" ? 4 : 1,
              )
            : new Date();

        const newEpoch: Epoch = {
          ...epoch,
          id: epochId,
          date: date.getTime(),
          type: epoch?.type || "full",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        const newEpochs = {
          ...prev[modelPropId],
          [epochId]: newEpoch,
        };
        return { ...prev, [modelPropId]: newEpochs };
      });

      return epochId;
    },
  );

  const deleteEpochAtom = atom(null, (_get, set, epochId: string) => {
    set(modelAtom, (prev) => {
      const newEpochs = { ...prev[modelPropId] };
      delete newEpochs[epochId];
      return { ...prev, [modelPropId]: newEpochs };
    });
  });

  return {
    modelPropId,
    orderedEpochsMolecule,
    createEpochAtom,
    deleteEpochAtom,
  };
};

export type EpochsAtomsContainer = ReturnType<typeof createEpochAtoms>;
export const loanEpochsAtomsContainer = createEpochAtoms("epochs");
export const workingCapitalEpochsAtomsContainer = createEpochAtoms(
  "workingCapitalEpochs",
);

export const indexOfLastFullYearEpochAtom = atom((get) => {
  const epochsMap = get(loanEpochsAtomsContainer.orderedEpochsMolecule);

  const fullYearEpochs = epochsMap.filter(
    (epoch) => get(epoch).type === "full",
  );

  if (fullYearEpochs.length === 0) {
    return 0;
  }

  return (
    epochsMap.findIndex(
      (epoch) =>
        get(epoch).id === get(fullYearEpochs[fullYearEpochs.length - 1]).id,
    ) || 0
  );
});

const convertStringToNumberHash = (str: string) => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
};

export const annualizeEpochAtom = atom(null, (get, set, epochId: string) => {
  const epochToCloneAtom = get(
    loanEpochsAtomsContainer.orderedEpochsMolecule,
  ).find((epochAtom) => get(epochAtom).id === epochId);

  if (!epochToCloneAtom) return;
  const epochToClone = get(epochToCloneAtom);

  const months = epochMonthCount(epochToClone);

  const data = {
    ...epochToClone,
    type: "full" as const,
    date: epochToClone.duration?.startDate || epochToClone.date,
  } as Omit<Epoch, "id">;

  const newEpochId = set(loanEpochsAtomsContainer.createEpochAtom, data);

  const model = get(modelAtom);

  Object.keys(model.cells).forEach((cellId) => {
    if (!cellId.includes(`epoch|${epochId}|`)) return;
    if (model.cells[cellId].type !== "dollar") return;
    if (typeof model.cells[cellId].value !== "number") return;

    const newCellId = cellId.replace(
      `epoch|${epochId}|`,
      `epoch|${newEpochId}|`,
    );

    model.cells[newCellId] = {
      ...model.cells[cellId],
      value: ((Number(model.cells[cellId].value) / months) * 12) as number,
    } as DollarCell;
  });

  set(modelAtom, model);

  // get new epoch atom
  const newEpochAtom = get(loanEpochsAtomsContainer.orderedEpochsMolecule).find(
    (epochAtom) => get(epochAtom).id === newEpochId,
  );
  if (!newEpochAtom) return;

  set(newEpochAtom, {
    ...get(newEpochAtom),
    updatedAt: Date.now(),
    date: data.date,
    annualized: true,
  });

  setTimeout(() => {
    set(newEpochAtom, {
      ...get(newEpochAtom),
      type: "full",
    });
  }, 10);
});

export const epochMonthCount = (epoch: Epoch) => {
  if (epoch.type === "full") return 12;

  if (!epoch.duration) return 0;

  return (
    (new Date(epoch.duration.endDate || "").getFullYear() -
      new Date(epoch.duration.startDate || "").getFullYear()) *
      12 +
    (new Date(epoch.duration.endDate || "").getMonth() -
      new Date(epoch.duration.startDate || "").getMonth()) +
    1
  );
};
